export default {
  common: {
    loading: 'Loading...',
    error: 'Error',
    success: 'Success',
    warning: 'Warning',
    info: 'Info',
    noData: 'No data available',
    submit: 'Submit',
    cancel: 'Cancel',
    save: 'Save',
    edit: 'Edit',
    delete: 'Delete',
    search: 'Search',
    filter: 'Filter',
    viewAll: 'View All',
    noResults: 'No results found',
    close: 'Close',
    back: 'Back',
    next: 'Next',
    previous: 'Previous',
    configureSettings: 'Configure Settings',
    noDataToDisplay: 'No data to display',
    loadingData: 'Loading data...',
    na: 'N/A',
    to: 'to',
    anonymous: 'Anonymous',
    expanded: 'Expanded content',
    expand: 'Expand text',
    collapse: 'Collapse text',
    results: 'Results',
    downloadCsv: 'Download CSV for Date Range',
    preparing: 'Preparing...',
  },
  app: {
    title: 'Veta Virtual Dashboard',
    logo: 'Veta Virtual logo',
    footer: {
      copyright: '© {{year}} Veta Virtual. All rights reserved.',
      version: 'Version {{version}}',
    },
  },
  dashboard: {
    title: 'Dashboard',
    summary: 'Overview of key metrics and performance',
    welcomeMessage: 'Welcome to your Veta Virtual dashboard',
    lastUpdated: 'Last updated: {{date}}',
  },
  home: {
    welcome:
      'Welcome to Veta Virtual! We are very excited that you decided to partner with us! This document outlines our procedures for how we deliver our services and other key information regarding your account.',
    dashboardInfo: 'Dashboard Info:',
    authentication:
      'Please authenticate with your given credentials. Once you are logged in, you can update your password.',
    usageTime:
      'Usage Time: Your usage time is calculated by adding the Call Time and After-Call Work Time (where we type the call notes and send them to you) which equals the Total Handle Time.',
    usageTotals: 'Usage totals are approximate and will not be used for billing purposes.',
    emailAddresses: 'Email Addresses',
    mainPhoneNumbers: 'Main Phone Numbers:',
    hoursOfOperation: 'Main Hours of Operation:',
    billingSchedule: 'Billing Schedule',
    holidaySchedule: 'Holiday Schedule',
    cancellationPolicy: 'Cancellation Policy Summary',
    version: 'VVRSP.2023.1.4 - 19 Sep 2023',
  },
  analytics: {
    title: 'Impact Overview',
    subtitle: 'Comprehensive analytics and performance metrics',
    infoMessage:
      'The usage minutes shown are only for reference purposes, final usage will be adjusted at the end of billing cycle.',
    timeZoneDisclaimer: 'Call times are displayed in Eastern Time Zone (EST).',
    viewingAll: 'Viewing metrics from all campaigns',
    viewingCampaign: 'Showing data for: {{campaign}}',
    loadingCampaign: 'Loading campaign...',
    noCampaignSelected: 'No campaign selected',
    dateRange: {
      title: 'Select Date Range',
      start: 'Start Date',
      end: 'End Date',
      apply: 'Apply',
      preset: {
        today: 'Today',
        yesterday: 'Yesterday',
        thisWeek: 'This Week',
        lastWeek: 'Last Week',
        thisMonth: 'This Month',
        lastMonth: 'Last Month',
        thisQuarter: 'This Quarter',
        lastQuarter: 'Last Quarter',
        thisYear: 'This Year',
        lastYear: 'Last Year',
        custom: 'Custom Range',
      },
    },
    charts: {
      title: 'Performance Charts',
      callsPerDay: 'Calls per Day',
      handleTimePerDay: 'Handle Time per Day',
      cssPerDay: 'CSS per Day',
      dispositionBreakdown: 'Disposition Breakdown',
      callDirection: 'Call Direction',
      noChartData: 'No chart data available for the selected date range',
    },
  },
  metrics: {
    titles: {
      dailyCallCount: 'Daily Call Count',
      dailyCallMinutes: 'Daily Call Minutes',
      hotLeads: 'Potential New Clients',
      totalCallCount: 'Total Call Count',
      totalCallMinutes: 'Usage Time (Minutes)',
      clientSatisfaction: 'Client Satisfaction',
      cssMonthToDate: 'CSS (Month-to-Date)',
      avgCssScore: 'Average CSS Score',
      planRemaining: 'Plan Remaining',
      planMinutes: 'Current Plan (Minutes)',
    },
    descriptions: {
      dailyCallCount: 'Total number of calls handled today.',
      dailyCallMinutes: 'Total minutes spent on calls today.',
      hotLeads:
        'Number of potential new clients and appointment bookings identified within the selected date range.',
      totalCallCount: 'Total number of calls handled within the selected date range.',
      totalCallMinutes: 'Total minutes spent on calls within the selected date range.',
      clientSatisfaction: 'Client Satisfaction Score based on call ratings.',
      cssMonthToDate: 'Client Satisfaction Score for the current month.',
      avgCssScore: 'Average Client Satisfaction Score across all campaigns.',
      planRemaining: 'Percentage of your monthly plan minutes remaining.',
      planMinutes: 'Total minutes allocated in your current monthly plan.',
    },
    sections: {
      todayMetrics: "Today's Metrics",
      dateRangeMetrics: 'Date Range Metrics',
      monthToDateMetrics: 'Month-to-Date Metrics',
    },
    minutesRemaining: '{{count}} minutes remaining',
    noMetricsAvailable: 'No Metrics Available',
    noCallsRecorded: 'There are no calls recorded for the selected date range:',
    expandDateRange: 'Try expanding your date range to include more call data.',
    metricsWillAppear: 'Metrics cards will automatically appear when data is available.',
    planRequest: {
      connectWithCxm: 'Connect with CXM',
      dialogTitle: 'Connect with Your Client Experience Manager',
      dialogDescription:
        "We'll connect you with your CXM to discuss your plan options and any specific needs you might have.",
      yourName: 'Your Name',
      companyName: 'Company Name',
      additionalNotes: 'Additional Notes (Optional)',
      notesPlaceholder: "Any specific questions or requirements you'd like to share...",
      submitting: 'Submitting...',
      submitRequest: 'Submit Request',
    },
    planRemainingTooltip: 'This figure updates every 24 hours.',
    currentMonth: 'Current Month to',
  },
  calls: {
    title: 'Call Log',
    callId: 'Call ID',
    callDate: 'Call Date',
    callTime: 'Call Time',
    direction: 'Direction',
    duration: 'Duration',
    disposition: 'Disposition',
    agent: 'Agent',
    email: 'Email',
    type: 'Call Type',
    campaign: 'Campaign',
    phoneNumber: 'Phone Number',
    details: 'Details',
    actions: 'Actions',
    viewDetails: 'View call details',
    noCallsFound: 'No calls found',
    totalCalls: 'Total Calls',
    inbound: 'Inbound',
    outbound: 'Outbound',
    unknown: 'Unknown',
    callSearch: 'Call Search',
    callSearchSubtitle: 'Search and review call records across campaigns',
    feedback: 'Feedback',
    searchModes: {
      campaignAndDate: 'Campaign & Date',
      callId: 'Call ID',
      dateOnly: 'Date Only',
      dateRange: 'Date Range',
    },
    sort: {
      title: 'Sort By',
      dateNewest: 'Date (Newest first)',
      dateOldest: 'Date (Oldest first)',
      callIdHighest: 'Call ID (Highest first)',
      callIdLowest: 'Call ID (Lowest first)',
      dispositionAZ: 'Disposition (A-Z)',
      feedbackUpvotes: 'Feedback (Upvotes first)',
      durationLongest: 'Duration (Longest first)',
      durationShortest: 'Duration (Shortest first)',
      clickToSort: 'Click to sort by this column',
    },
    table: {
      rating: 'Rating',
      timestamp: 'Date',
      direction: 'Direction',
      callType: 'Call Type',
      from: 'From',
      to: 'To',
      customerName: 'Customer Name',
      customerCompany: 'Customer Company',
      agentName: 'Agent Name',
      talkTime: 'Talk Time',
      handleTime: 'Handle Time',
      handleTimeTooltip: 'Handle time equals to talk time plus after call work time',
      callNote: 'Call Note',
      resetSorting: 'Reset Sorting',
      exportCsv: 'Export CSV',
      exporting: 'Exporting...',
    },
    drawer: {
      title: 'Call Details',
      noDetailsAvailable: 'No details available.',
      callId: 'Call ID:',
      tabs: {
        overview: 'Overview',
        summary: 'AI Summary',
        transcript: 'Transcript',
        sentiment: 'Sentiment Analysis',
      },
      summary: {
        title: 'Call Summary',
        noSummary: 'No AI Summary Available for this call.',
        analyzing: 'Analyzing call content...',
        processingDescription:
          'Our AI is processing the audio and generating a comprehensive summary',
        notAvailableTitle: 'AI Summary Not Available',
        notAvailableMessage:
          'AI Summary is only available for calls from 2025 onwards. This call is from an earlier date and cannot be processed with our AI system.',
      },
      transcript: {
        title: 'Transcript details',
        noTranscript: 'No transcript available for this call.',
        downloadTranscript: 'Download Call Transcript',
      },
      sentimentAnalysis: {
        title: 'Call Sentiment Analysis',
        noAnalysis: 'No sentiment data available for this call.',
      },
      callInformation: 'Call Information',
      participants: 'Participants',
      callNotes: 'Call Notes',
      rateThisCall: 'Rate this call:',
      feedback: {
        good: 'Keep It Up!',
        bad: 'Needs Improvement',
      },
      comments: {
        title: 'Comments',
        noComments: 'No comments yet. Be the first to add a comment!',
        addYourFeedback: 'Add Your Feedback',
        addComment: 'Add a comment',
        commentPlaceholder: 'Share your thoughts about this call...',
        send: 'Send',
        save: 'Save',
        cancel: 'Cancel',
        anonymous: 'Anonymous',
      },
    },
  },
  campaigns: {
    title: 'Campaigns',
    name: 'Name',
    status: 'Status',
    callCount: 'Call Count',
    totalMinutes: 'Total Minutes',
    avgHandleTime: 'Avg. Handle Time',
    satisfactionScore: 'Satisfaction Score',
    active: 'Active',
    inactive: 'Inactive',
    noCampaignsFound: 'No campaigns found',
  },
  auth: {
    login: 'Log In',
    logout: 'Log Out',
    profile: 'Profile',
    settings: 'Settings',
    welcome: 'Welcome, {{name}}!',
    notAuthenticated: 'Not authenticated',
    userModeForced: '(User Mode Forced)',
    profilePicture: "{{name}}'s profile picture",
  },
  navigation: {
    home: 'Home',
    metrics: 'Metrics', // Keeping for backward compatibility
    campaigns: 'Campaigns',
    callSearch: 'Call Search',
    callDashboard: 'Call Dashboard',
    vetaMainline: 'Veta Mainline',
    glossary: 'Glossary',
    settings: 'Settings',
  },
  errors: {
    fetchFailed: 'Failed to fetch data.',
    genericError: 'Something went wrong. Please try again.',
    noPermission: 'You do not have permission to view this resource.',
    required: '{{field}} is required',
    formValidation: 'Please fill in all required fields',
    transcriptDownload: 'Unable to identify call ID for transcript download',
  },
  glossary: {
    title: 'Glossary',
    subtitle: 'Terms and definitions used across the Veta Virtual platform.',

    terms: {
      messageTaken: {
        title: 'Message Taken',
        description:
          "The Engagement Specialist captured the caller's details and reason for calling so staff can return the call.",
      },

      solicitationNcn: {
        title: 'Solicitation - NCN',
        description:
          'A sales or marketing call deemed non-client-related (No call Note). Includes things like spam calls from telemarketers.',
      },

      notRelevantNcn: {
        title: 'Not Relevant - NCN',
        description:
          "Call content was unrelated to the client's services. No Call Note (NCN). Includes prank calls or inquiries intended for a different business.",
      },

      transferredCall: {
        title: 'Transferred Call',
        description:
          'The Engagement Specialist successfully routed the caller to the requested staff member or extension.',
      },

      voicemail: {
        title: 'Voicemail',
        description:
          'The caller was sent to voicemail, leaving (or having the option to leave) a recorded message.',
      },

      appointmentBooked: {
        title: 'Appointment Booked',
        description: 'The caller successfully scheduled an appointment with the client.',
      },

      potentialNewClient: {
        title: 'Potential New Client',
        description:
          "The caller expressed interest in becoming a client or inquired about the client's services.",
      },

      callerQuestionAnswered: {
        title: 'Caller Question Answered',
        description:
          "The Engagement Specialist resolved the caller's inquiry on the spot, requiring no further follow-up.",
      },

      urgentCall: {
        title: 'Urgent Call',
        description: 'A call that requires immediate attention or action from the client.',
      },

      afterCallWork: {
        title: 'After Call Work',
        description:
          'Tasks completed by the agent after a call, such as writing call notes or sending follow-up emails.',
      },

      handleTime: {
        title: 'Handle Time',
        description: 'The total time spent on a call, including talk time and after-call work.',
      },

      talkTime: {
        title: 'Talk Time',
        description: 'The actual time spent speaking with the caller during a call.',
      },
    },
  },
  importQA: {
    title: 'Import QA Results',
    subtitle:
      'Upload QA evaluation results from a CSV file (e.g., Google Form responses) to import them into the dashboard.',
    uploadTitle: 'Upload CSV File',
    uploadDescription:
      'Select a CSV file exported from the QA Process Google Form. The file should include columns for Call ID, Agent email, Campaign Name, criteria scores (1.1-5.5), and the overall Result.',
    dropZoneText: 'Drag & drop your CSV file here',
    dropZoneBrowse: 'or click to browse files',
    csvFileRequired: 'Please select a CSV file',
    processing: 'Processing...',
    uploadPreview: 'Upload & Preview',
    summaryTitle: 'Import Summary',
    totalRows: 'Total Rows',
    readyToImport: 'Ready to Import',
    alreadyImported: 'Already Imported',
    skipped: 'Skipped',
    rowsWithCallId: 'Rows with Call ID',
    rowsWithoutCallId: 'Rows without Call ID',
    willBeSkipped: 'will be skipped',
    matchedInDb: 'Matched in DB',
    notFoundInDb: 'Not found in DB',
    campaigns: 'Campaigns',
    dateRange: 'Date range',
    duplicateCallIds: 'Duplicate Call IDs in CSV',
    noCallIdWarning: '{{count}} row(s) have no Call ID and will be skipped.',
    unmatchedWarning:
      '{{count}} row(s) have a Call ID not found in the database and will be skipped.',
    overwriteLabel: 'Overwrite {{count}} existing QA response(s)',
    overwriteOn: 'Existing responses will be replaced with CSV data',
    overwriteOff: 'Existing responses will be kept (duplicates skipped)',
    previewTitle: 'Preview (first {{count}} rows)',
    cancel: 'Cancel',
    importCount: 'Import {{count}} Result',
    importCountPlural: 'Import {{count}} Results',
    importing: 'Importing QA Results...',
    importingDescription: 'Please wait while the results are being imported into the database.',
    importComplete: 'Import Complete',
    imported: 'Imported',
    updated: 'Updated',
    errors: 'Errors',
    errorsWarning: '{{count}} row(s) had errors:',
    andMore: '... and {{count}} more',
    viewDetails: 'View Details',
    uploadAnother: 'Upload Another File',
    status: {
      ready: 'Ready',
      alreadyImported: 'Already Imported',
      notInDb: 'Not in DB',
      noCallId: 'No Call ID',
    },
    table: {
      callId: 'Call ID',
      agent: 'Agent',
      campaign: 'Campaign',
      date: 'Date',
      grade: 'Grade',
      result: 'Result',
      status: 'Status',
      row: 'Row',
      reason: 'Reason',
      error: 'Error',
    },
    dialog: {
      title: 'Import Results',
      noRows: 'No rows in this category',
      exportCsv: 'Export CSV',
      close: 'Close',
    },
    headerWarningsTitle: 'Column mapping notes:',
    config: {
      title: 'Column Mapping Configuration',
      subtitle: 'Configure how CSV columns map to QA evaluation fields',
      criteria: 'Criteria Columns',
      criteriaDesc:
        'Map criterion keys (e.g., 1.1, 4.3) to the CSV header text for each QA question.',
      sectionNotes: 'Section Notes',
      sectionNotesDesc: 'Map section note keys to their CSV header text.',
      metadata: 'Metadata Columns',
      metadataDesc:
        'Map additional metadata fields from the CSV (phone number, disposition, team, etc.).',
      scores: 'Score Columns',
      scoresDesc: 'Map score and weight columns from the CSV.',
      results: 'Result Columns',
      resultsDesc: 'Map result columns (grade, pass/fail, overall result, strength).',
      key: 'Key',
      csvHeader: 'CSV Column Header',
      headerPlaceholder: 'Enter the CSV column header text',
      remove: 'Remove',
      addCriteria: 'Add Criteria',
      save: 'Save Configuration',
      saved: 'Configuration saved successfully',
      unsaved: 'Unsaved',
      detectFromCsv: 'Detect from CSV',
      detected: 'Headers detected and mappings updated. Review and save.',
      detectedWithUnmatched: '{{count}} CSV column(s) could not be auto-matched. Review and save.',
    },
  },
  importCalls: {
    uploadTitle: 'Upload Vendor Call CSV',
    uploadDescription:
      'Upload the weekly call report shared by the third-party answering service. Calls are added to the dashboard with the agent shown as "24H - [Agent Name]" so you can tell which calls the outside company took. Required columns: TIMESTAMP, CAMPAIGN, AGENT, ANI, DNIS, plus optional talk/handle time and notes.',
    dropZoneText: 'Drag & drop the vendor CSV here',
    dropZoneBrowse: 'or click to browse files',
    csvFileRequired: 'Please select a CSV file',
    previewFailed:
      'Could not preview the file. The server may be restarting — please try again in a moment.',
    importFailed:
      'Could not import the calls. The server may be restarting — please try again in a moment.',
    processing: 'Processing...',
    uploadPreview: 'Upload & Preview',
    summaryTitle: 'Import Summary',
    totalRows: 'Total Rows',
    newCalls: 'New Calls',
    replaceCalls: 'Will Replace',
    existingCalls: 'Will Update',
    skipped: 'Skipped',
    campaigns: 'Campaigns',
    newCampaigns: '{{count}} new campaign(s) will be created',
    agents: 'Agents',
    dateRange: 'Date range',
    duplicateInFile: '{{count}} duplicate row(s) within the file (collapsed on import)',
    invalidWarning:
      '{{count}} row(s) are missing a campaign or a valid timestamp and will be skipped.',
    updateNotice: '{{count}} call(s) already exist and will be updated in place.',
    replaceNotice:
      '{{count}} after-hours call(s) already logged by Five9 with 0 handle time will be replaced by the 24H record — no duplication.',
    previewTitle: 'Preview (first {{count}} rows)',
    cancel: 'Cancel',
    importCount: 'Import {{count}} Call',
    importCountPlural: 'Import {{count}} Calls',
    importing: 'Importing Calls...',
    importingDescription: 'Please wait while the calls are being saved to the dashboard.',
    importComplete: 'Import Complete',
    imported: 'Imported',
    replaced: 'Replaced',
    updated: 'Updated',
    campaignsCreated: 'Campaigns Created',
    errorsWarning: '{{count}} row(s) had errors:',
    andMore: '... and {{count}} more',
    uploadAnother: 'Upload Another File',
    status: {
      new: 'New',
      replace: 'Will Replace',
      duplicate: 'Will Update',
      invalid: 'Skipped',
    },
    table: {
      timestamp: 'Timestamp',
      campaign: 'Campaign',
      agent: 'Agent',
      customer: 'Customer',
      disposition: 'Disposition',
      ani: 'ANI',
      status: 'Status',
      row: 'Row',
    },
  },
}
